import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import QRCode from 'qrcode.react';
import { useAuth } from '../contexts/AuthContext';

interface Location {
  latitude: number;
  longitude: number;
  accuracy?: number;
  timestamp: string; // Epoch timestamp (milliseconds since Unix epoch)
}

interface QRCodeData {
  clientId: string;
  timestamp: string; // Epoch timestamp (milliseconds since Unix epoch)
  clientLocation: Location;
  qrCodeUrl: string;
}

export interface QRCodeDisplayRef {
  refresh: () => void;
}

const QRCodeDisplay = forwardRef<QRCodeDisplayRef>((props, ref) => {
  const { user } = useAuth();
  const [qrCodeData, setQrCodeData] = useState<QRCodeData | null>(null);
  const [location, setLocation] = useState<Location | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Get current location
  const getCurrentLocation = useCallback((): Promise<Location> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location: Location = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: Date.now().toString()
          };
          resolve(location);
        },
        (error) => {
          let errorMessage = 'Failed to get location';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location permission denied. Please enable location access.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information unavailable.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out.';
              break;
          }
          reject(new Error(errorMessage));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000 // 1 minute cache
        }
      );
    });
  }, []);

  // Generate QR code data
  const generateQRCode = useCallback(async () => {
    try {
      setIsLoading(true);
      setLocationError(null);

      // Get current location
      const currentLocation = await getCurrentLocation();
      setLocation(currentLocation);

      // Generate epoch timestamp (milliseconds since Unix epoch)
      const timestamp = Date.now().toString();
      
      // Create QR code URL as specified in UC-001
      const baseUrl = window.location.origin;
      const qrCodeUrl = `${baseUrl}/checkin/${user?.userId}/${timestamp}`;

      const newQRCodeData: QRCodeData = {
        clientId: user?.userId || '',
        timestamp,
        clientLocation: currentLocation,
        qrCodeUrl
      };

      setQrCodeData(newQRCodeData);
      
      // Log the QR code data for debugging (in production, this would be sent to backend)
      console.log('QR Code generated with location:', newQRCodeData);
      console.log('QR Code URL:', newQRCodeData.qrCodeUrl);
      console.log('Client Location:', newQRCodeData.clientLocation);

    } catch (error) {
      console.error('Error generating QR code:', error);
      setLocationError(error instanceof Error ? error.message : 'Failed to get location');
    } finally {
      setIsLoading(false);
    }
  }, [user?.userId, getCurrentLocation]);

  // Expose refresh method via ref
  useImperativeHandle(ref, () => ({
    refresh: generateQRCode
  }));

  // Auto-refresh QR code every minute as specified in UC-001
  useEffect(() => {
    generateQRCode();

    const interval = setInterval(() => {
      generateQRCode();
    }, 60000); // 1 minute

    return () => clearInterval(interval);
  }, [generateQRCode]);

  // Manual refresh handler
  const handleRefresh = () => {
    generateQRCode();
  };

  if (isLoading) {
    return (
      <div className="bg-gray-100 rounded-lg p-8 text-center">
        <div className="w-64 h-64 mx-auto bg-white rounded-lg border-2 border-gray-300 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
            <p className="text-gray-500 text-sm">Getting location...</p>
          </div>
        </div>
      </div>
    );
  }

  if (locationError) {
    return (
      <div className="bg-gray-100 rounded-lg p-8 text-center">
        <div className="w-64 h-64 mx-auto bg-white rounded-lg border-2 border-gray-300 flex items-center justify-center">
          <div className="text-center">
            <div className="text-red-500 mb-2">
              <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <p className="text-red-600 text-sm mb-2">Location Error</p>
            <p className="text-gray-500 text-xs mb-4">{locationError}</p>
            <button
              onClick={handleRefresh}
              className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!qrCodeData) {
    return (
      <div className="bg-gray-100 rounded-lg p-8 text-center">
        <div className="w-64 h-64 mx-auto bg-white rounded-lg border-2 border-gray-300 flex items-center justify-center">
          <p className="text-gray-500">Failed to generate QR code</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 rounded-lg p-8 text-center">
      <div className="w-64 h-64 mx-auto bg-white rounded-lg border-2 border-gray-300 flex items-center justify-center p-4">
        <QRCode
          value={qrCodeData.qrCodeUrl}
          size={240}
          level="M"
          includeMargin={true}
          renderAs="svg"
        />
      </div>
      
      {/* Location Info */}
      <div className="mt-4 text-sm text-gray-600">
        <p className="mb-2">
          <strong>Location captured:</strong>
        </p>
        <p className="text-xs">
          Lat: {qrCodeData.clientLocation.latitude.toFixed(6)}
        </p>
        <p className="text-xs">
          Lng: {qrCodeData.clientLocation.longitude.toFixed(6)}
        </p>
        {qrCodeData.clientLocation.accuracy && (
          <p className="text-xs">
            Accuracy: ±{Math.round(qrCodeData.clientLocation.accuracy)}m
          </p>
        )}
        <p className="text-xs mt-1">
          Generated: {new Date(parseInt(qrCodeData.timestamp)).toLocaleTimeString()}
        </p>
      </div>

      {/* Refresh Button */}
      <button
        onClick={handleRefresh}
        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 transition-colors"
      >
        Refresh QR Code
      </button>

      <p className="mt-4 text-xs text-gray-500">
        QR code refreshes automatically every minute
      </p>
    </div>
  );
});

export default QRCodeDisplay; 